/**
 * Auto-nav-kandidater: kjappe punkt-markeringer gjort MENS man kjører (auto-nav).
 *
 * Under kjøring kan man ikke fylle ut et skjema — så et kart-trykk lagrer bare et
 * råt koordinat her. Kvalitetssikringen skjer i ETTERTID i en «Til gjennomgang»-liste,
 * der brukeren justerer plassering + fyller info via det vanlige AddSpotModal og
 * eventuelt deler plassen med fellesskapet. Kandidatene er private til da.
 *
 * Lagres i /users/{uid}/autonav_candidates/{id} (owner-only, som private_spots).
 * Bevisst en EGEN samling — kandidatene skal ikke blande seg inn i `spots`/kartet
 * som ekte plasser før de er gjennomgått.
 */
import { collection, doc, addDoc, deleteDoc, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from './firebaseService';
import type { Coordinates } from '../types';

export interface AutonavCandidate {
  id: string;
  location: Coordinates;
  /** Opprettet-tidspunkt i ms (klient-satt) — brukes til sortering + påminnelse. */
  createdAt: number;
  /** Sist sendt påminnelse (ms). Settes av server-funksjonen for å unngå spam. */
  remindedAt?: number;
}

const colRef = (uid: string) => collection(db, 'users', uid, 'autonav_candidates');

/** Lagre et råt kandidat-punkt. Returnerer ny id. */
export const addAutonavCandidate = async (uid: string, location: Coordinates): Promise<string | null> => {
  if (!uid || !location || !Number.isFinite(location.latitude) || !Number.isFinite(location.longitude)) {
    return null;
  }
  const ref = await addDoc(colRef(uid), {
    location: { latitude: location.latitude, longitude: location.longitude },
    createdAt: Date.now(),
  });
  return ref.id;
};

/** Sanntids-lytter på brukerens kandidater, nyeste først. */
export const subscribeToAutonavCandidates = (
  uid: string,
  cb: (candidates: AutonavCandidate[]) => void,
): (() => void) => {
  if (!uid) { cb([]); return () => {}; }
  const q = query(colRef(uid), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    (snap) => {
      cb(snap.docs.map((d) => ({ ...(d.data() as Omit<AutonavCandidate, 'id'>), id: d.id })));
    },
    (err) => {
      console.warn('[autonavCandidates] onSnapshot failed:', err);
      cb([]);
    },
  );
};

/** Slett en kandidat (etter gjennomgang, eller forkastet). */
export const deleteAutonavCandidate = async (uid: string, id: string): Promise<void> => {
  if (!uid || !id) return;
  await deleteDoc(doc(db, 'users', uid, 'autonav_candidates', id));
};
